import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupCardItem } from "@/components/ui/radio-group";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleBuyInCustomer } from "../_helpers/vehicle-buy-in-form.helpers";

type CustomerIdentifier = NonNullable<VehicleBuyInCustomer["customerIdentifiers"]>[number];

interface VehicleBuyInCustomerNumberDialogProps {
  identifiers: CustomerIdentifier[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (customerNumber: string) => void;
}

export function VehicleBuyInCustomerNumberDialog({
  identifiers,
  open,
  onOpenChange,
  onSelect,
}: VehicleBuyInCustomerNumberDialogProps) {
  const { t } = useTranslation(["fab", "common", "masterdata"]);
  const [selectedIdentifierUuid, setSelectedIdentifierUuid] = useState("");

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedIdentifierUuid("");
    }
    onOpenChange(nextOpen);
  };

  const handleSelect = () => {
    const selectedIdentifier = identifiers.find((identifier) => identifier.uuid === selectedIdentifierUuid);
    if (!selectedIdentifier) return;

    onSelect(selectedIdentifier.identifier);
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("fab:SelectCustomerNumber")}</DialogTitle>
          <DialogDescription>{t("fab:SelectCustomerNumberDescription")}</DialogDescription>
        </DialogHeader>
        <RadioGroup value={selectedIdentifierUuid} onValueChange={setSelectedIdentifierUuid}>
          {identifiers.map((identifier) => (
            <RadioGroupCardItem
              key={identifier.uuid}
              value={identifier.uuid}
              label={t(`masterdata:DmsSystem${identifier.type}`)}
              description={identifier.identifier}
              selected={identifier.uuid === selectedIdentifierUuid}
              className="*:data-[slot=radio-group-item]:sr-only"
            />
          ))}
        </RadioGroup>
        <DialogFooter>
          <Button
            type="button"
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={() => handleOpenChange(false)}
          >
            {t("common:Cancel")}
          </Button>
          <Button type="button" disabled={!selectedIdentifierUuid} onClick={handleSelect}>
            {t("fab:ApplyCustomerNumber")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
